import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
  ModalSheetSection,
  ModalSheetTitle,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import styles from './BulkDuplicateBundleDialog.module.css'
import { Tooltip } from '@/components/Tooltip'
import { Icon } from '@/components/Icon'
import type { Id } from '@/infra/id'

export interface BulkDuplicateBundleItem {
  id: Id
  name: string
  description: string
  baseItemName: string
}

export interface BulkDuplicatePartiallyCompatibleBundleItem extends BulkDuplicateBundleItem {
  incompatibleItemNames: string[]
}

export interface BulkDuplicateBundleDialogProps {
  showing: boolean
  onClose: () => unknown
  selectedBundlesCount: number
  workspaceDropdown: ReactNode
  incompatibleBundles: BulkDuplicateBundleItem[]
  partiallyCompatibleBundles: BulkDuplicatePartiallyCompatibleBundleItem[]
  compatibleBundlesCount: number
  onSubmit: () => unknown
  isSubmitPending: boolean
  submitButtonTestId?: string
}

interface BundleItemCompatibilityProps {
  bundle: BulkDuplicateBundleItem
  compatibility: string
  items?: string[]
}

function BundleItemCompatibility({
  bundle,
  compatibility,
  items,
}: BundleItemCompatibilityProps) {
  const isPartial = items && items.length > 0
  const toolTipContent = isPartial
    ? `These items will be excluded: ${items?.join(', ')}`
    : `The base item is incompatible: ${bundle.baseItemName}`

  return (
    <div className={styles.itemContainer}>
      <div className={styles.itemTextContainer}>
        <div className={styles.title}>{bundle.name}</div>
        <div className={styles.description}>{bundle.description}</div>
      </div>

      <span className={styles.itemCompatibility}>
        {compatibility}

        <Tooltip content={toolTipContent}>
          <Icon
            name="info"
            size={18}
            color={isPartial ? 'var(--grey700)' : 'var(--red500)'}
          />
        </Tooltip>
      </span>
    </div>
  )
}

export function BulkDuplicateBundleDialog({
  showing,
  onClose,
  selectedBundlesCount,
  workspaceDropdown,
  incompatibleBundles,
  partiallyCompatibleBundles,
  compatibleBundlesCount,
  onSubmit,
  isSubmitPending,
  submitButtonTestId,
}: BulkDuplicateBundleDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader bordered withCloseButton>
        <ModalSheetTitle>
          Duplicate selected bundles ({selectedBundlesCount})
        </ModalSheetTitle>
      </ModalSheetHeader>

      <ModalSheetSection spaced>{workspaceDropdown}</ModalSheetSection>

      {(incompatibleBundles.length > 0 ||
        partiallyCompatibleBundles.length > 0) && (
        <>
          <div className={styles.compatibilityHeader}>
            <span>Bundle</span>

            <span className={styles.itemCompatibility}>
              Compatibility
              <Tooltip
                content={
                  "Incompatible Bundles can't be duplicated, Partial means it would be duplicated without some service items"
                }
                placement="top"
              >
                <Icon name="info" size={18} color="var(--blue400)" />
              </Tooltip>
            </span>
          </div>

          <div className={styles.itemListContainer}>
            {incompatibleBundles.map((bundle) => (
              <BundleItemCompatibility
                key={bundle.id}
                bundle={bundle}
                compatibility="Incompatible"
              />
            ))}
            {partiallyCompatibleBundles.map((bundle) => (
              <BundleItemCompatibility
                key={bundle.id}
                bundle={bundle}
                compatibility="Partial"
                items={bundle.incompatibleItemNames}
              />
            ))}
          </div>
        </>
      )}

      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            isDisabled={isSubmitPending}
            onPress={onClose}
          />

          <Button
            label={`Duplicate ${compatibleBundlesCount || ''}`}
            variant="primary"
            testId={submitButtonTestId}
            onPress={onSubmit}
            isDisabled={isSubmitPending || compatibleBundlesCount === 0}
            isPending={isSubmitPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
